import { fromJS, Map, OrderedSet } from 'immutable';

import { TypedMap } from 'utils/typed-map';

import {
  APPROVE,
  DENY,
  FETCH_FAILED,
  FETCH_SUCCESS,
  MATCHING_REQUEST_STATUSES,
  RESPOND_FAILED,
  RESPOND_SUCCESS,
  SET_DISPLAYED_DONATION_IDS,
  SET_FETCH_PARAMS,
  SET_TOTAL,
} from './types';
import type { ExternalDonationRequestsActions } from './types';

export type FetchParams = {
  pageNum: number;
  limit: number;
  matchingRequestStatus: MATCHING_REQUEST_STATUSES;
  sort: unknown;
  order: 'asc' | 'desc';
  confidenceScore: string[] | null;
  search: string | null;
};
type MutableState = {
  loading: boolean;
  error: unknown;
  statusUpdating: {};
  fetchParams: FetchParams;
  total: number;
  displayedDonationIds: OrderedSet<string>;
};

export type ExternalDonationRequestsState = Map<string, any> & TypedMap<MutableState>;

const initialMutableState: MutableState = {
  loading: true,
  error: null,
  statusUpdating: {},
  fetchParams: {
    pageNum: 0,
    limit: 10,
    matchingRequestStatus: MATCHING_REQUEST_STATUSES.pending,
    // @NOTE-AC
    // This is not playing well
    // with the immutable.Map type
    // which already has a `sort` method
    sort: null,
    order: 'asc',
    confidenceScore: null,
    search: null,
  },
  total: 0,
  displayedDonationIds: OrderedSet<string>(),
};

export const initialState: ExternalDonationRequestsState = fromJS(initialMutableState);

export default (
  state: ExternalDonationRequestsState = initialState,
  action: ExternalDonationRequestsActions
): ExternalDonationRequestsState => {
  switch (action.type) {
    case APPROVE:
      return state.mergeIn(
        ['statusUpdating'],
        action.donationIds.reduce<Record<string, boolean>>((acc, id) => {
          acc[id] = true;
          return acc;
        }, {})
      );

    case DENY:
      return state.mergeIn(
        ['statusUpdating'],
        action.donationIds.reduce<Record<string, boolean>>((acc, id) => {
          acc[id] = true;
          return acc;
        }, {})
      );

    case RESPOND_SUCCESS:
      return state.mergeIn(
        ['statusUpdating'],
        action.donationIds.reduce<Record<string, boolean>>((acc, id) => {
          acc[id] = false;
          return acc;
        }, {})
      );

    case RESPOND_FAILED:
      return state.mergeIn(
        ['statusUpdating'],
        action.donationIds.reduce<Record<string, boolean>>((acc, id) => {
          acc[id] = false;
          return acc;
        }, {})
      );

    case SET_FETCH_PARAMS:
      // eslint-disable-next-line no-case-declarations
      const fetchParams: Map<string, any> & TypedMap<FetchParams> = state.get('fetchParams');
      return state.merge({
        loading: true,
        fetchParams: fetchParams.merge({ pageNum: 0, ...action.params }),
      }) as ExternalDonationRequestsState;

    case SET_TOTAL:
      return state.set('total', action.total) as ExternalDonationRequestsState;

    case SET_DISPLAYED_DONATION_IDS:
      return state.set('displayedDonationIds', action.donationIds) as ExternalDonationRequestsState;

    case FETCH_SUCCESS:
      return state.set('loading', false) as ExternalDonationRequestsState;

    case FETCH_FAILED:
      return state.merge({
        loading: false,
        error: action.error,
      }) as ExternalDonationRequestsState;

    default:
      return state;
  }
};
